import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useSnackbar } from "notistack";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import TuneIcon from "@mui/icons-material/Tune";
import SettingsIcon from "@mui/icons-material/Settings";

import Holder from "../../Components/HOC/Holder";
import CustomBtn from "../../Components/Reusable/CustomBtn";
import LoadBtn from "../../Components/Reusable/LoadBtn";
import WorkspaceTabs from "../../Components/Workspace/WorkspaceTabs";
import { activeWorkspaceState, authState, featuresState, popupState } from "../../Recoil/RecoilState";
import { baseURL, featureKeysArr } from "../../utils/StaticVariables";
import { errorDetail } from "./WorkspaceMembers";

// Form to add a new Workspace (Name, Description, Features — no active/inactive toggles)
export const CreateWorkspaceForm = ({ onSuccess }) => {
  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);
  const { enqueueSnackbar } = useSnackbar();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [selectedFeatures, setSelectedFeatures] = useState(
    featureKeysArr.map((f) => f.value)
  );

  const toggleFeature = (val) => {
    setSelectedFeatures((prev) =>
      prev.includes(val) ? prev.filter((k) => k !== val) : [...prev, val]
    );
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().min(1, "Name is required").max(100, "Max 100 characters").required("Workspace name is required"),
      description: Yup.string().max(500, "Max 500 characters"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          name: values.name.trim(),
          description: values.description.trim(),
          enabled_features: selectedFeatures,
        };
        const { data } = await axios.post(`${baseURL}workspaces`, payload, auth);
        const createdId = data?.workspace_id || data?.id;
        if (createdId && selectedFeatures?.length > 0) {
          try {
            await axios.put(
              `${baseURL}admin/workspaces/${createdId}/features`,
              { features: selectedFeatures },
              auth
            );
          } catch {
            // best-effort feature provision
          }
        }
        enqueueSnackbar("Workspace created successfully", { variant: "success" });
        window.dispatchEvent(new Event("workspaces-updated"));
        setPopup((p) => ({ ...p, isOpen: false }));
        if (onSuccess) onSuccess(data);
      } catch (error) {
        enqueueSnackbar(errorDetail(error, "Failed to create workspace"), { variant: "error" });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box component="form" onSubmit={formik.handleSubmit} noValidate>
      <Stack spacing={2.5}>
        <TextField
          label="Workspace Name"
          name="name"
          placeholder="e.g. Main Branch, Warehouse 1"
          value={formik.values.name}
          onChange={formik.handleChange}
          error={formik.submitCount > 0 && Boolean(formik.errors.name)}
          helperText={formik.submitCount > 0 && formik.errors.name}
          fullWidth
          required
          autoFocus
        />

        <TextField
          label="Description (Optional)"
          name="description"
          placeholder="Short description of this workspace"
          value={formik.values.description}
          onChange={formik.handleChange}
          error={formik.submitCount > 0 && Boolean(formik.errors.description)}
          helperText={formik.submitCount > 0 && formik.errors.description}
          fullWidth
          multiline
          minRows={2}
        />

        <Box>
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Enable AI Features
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Select features available for this workspace.
          </Typography>
          <Grid container spacing={1}>
            {featureKeysArr.map(({ value, label }) => {
              const isChecked = selectedFeatures.includes(value);
              return (
                <Grid item xs={12} sm={6} key={value}>
                  <Paper
                    variant="outlined"
                    onClick={() => toggleFeature(value)}
                    sx={{
                      p: 1,
                      px: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      borderRadius: 1.5,
                      borderColor: isChecked ? "primary.main" : "divider",
                      bgcolor: isChecked ? "action.hover" : "transparent",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <Typography variant="body2" fontWeight={isChecked ? 600 : 400}>
                      {label}
                    </Typography>
                    <Checkbox
                      size="small"
                      checked={isChecked}
                      onChange={() => toggleFeature(value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
          <LoadBtn loading={formik.isSubmitting} text="Create Workspace" />
        </Stack>
      </Stack>
    </Box>
  );
};

const WorkspaceSettings = () => {
  const { token } = useRecoilValue(authState);
  const [activeWorkspace, setActiveWorkspace] = useRecoilState(activeWorkspaceState);
  const [features, setFeatures] = useRecoilState(featuresState);
  const setPopup = useSetRecoilState(popupState);
  const { enqueueSnackbar } = useSnackbar();

  const [loadingDetails, setLoadingDetails] = useState(false);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const workspaceId = activeWorkspace?.workspace_id || activeWorkspace?.id;
  
  const [workspaceData, setWorkspaceData] = useState({
    name: activeWorkspace?.name || "",
    description: activeWorkspace?.description || "",
    enabled_features: activeWorkspace?.enabled_features || [],
  });

  const [selectedFeatures, setSelectedFeatures] = useState([]);

  // Fetch current workspace details
  const fetchWorkspaceDetails = useCallback(async () => {
    if (!workspaceId || !token) return;
    setLoadingDetails(true);
    try {
      const { data } = await axios.get(`${baseURL}workspaces/active`, auth);
      if (data) {
        const activeList = data.enabled_features?.length > 0
          ? data.enabled_features
          : Array.isArray(features) ? features : [];

        setWorkspaceData({
          name: data.name || "",
          description: data.description || "",
          enabled_features: activeList,
        });
        setSelectedFeatures(activeList);
      }
    } catch {
      // Fallback to activeWorkspace state
      const fallbackList = activeWorkspace?.enabled_features?.length > 0
        ? activeWorkspace.enabled_features
        : Array.isArray(features) ? features : [];

      setWorkspaceData((prev) => ({
        ...prev,
        name: activeWorkspace?.name || prev.name,
        description: activeWorkspace?.description || prev.description,
        enabled_features: fallbackList,
      }));
      setSelectedFeatures(fallbackList);
    } finally {
      setLoadingDetails(false);
    }
  }, [workspaceId, token, features, activeWorkspace]);

  useEffect(() => {
    fetchWorkspaceDetails();
  }, [fetchWorkspaceDetails]);

  // Handle General Info form (name, description only — no active/inactive)
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: workspaceData.name || "",
      description: workspaceData.description || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().min(1, "Name is required").max(100, "Max 100 characters").required("Name is required"),
      description: Yup.string().max(500, "Max 500 characters"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          name: values.name.trim(),
          description: values.description.trim(),
          enabled_features: selectedFeatures,
        };
        await axios.put(`${baseURL}workspaces/${workspaceId}`, payload, auth);
        enqueueSnackbar("Workspace settings updated successfully", { variant: "success" });
        setActiveWorkspace((prev) => ({
          ...prev,
          name: values.name.trim(),
          description: values.description.trim(),
        }));
        setWorkspaceData((prev) => ({
          ...prev,
          name: values.name.trim(),
          description: values.description.trim(),
        }));
        window.dispatchEvent(new Event("workspaces-updated"));
      } catch (error) {
        enqueueSnackbar(errorDetail(error, "Failed to update workspace settings"), { variant: "error" });
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Toggle single feature
  const handleToggleFeature = (featureKey) => {
    console.log('Toggling feature:', featureKey);
    setSelectedFeatures((prev) =>
      prev.includes(featureKey)
        ? prev.filter((k) => k !== featureKey)
        : [...prev, featureKey]
    );
  };

  // Toggle all features
  const handleToggleAll = () => {
    if (selectedFeatures.length === featureKeysArr.length) {
      setSelectedFeatures([]);
    } else {
      setSelectedFeatures(featureKeysArr.map((f) => f.value));
    }
  };

  // Save workspace features
  const handleSaveFeatures = async () => {

    console.log('workspaceId',workspaceId)
  if (!workspaceId) return;

  setSavingFeatures(true);

  try {
    await axios.put(
      `${baseURL}admin/workspaces/${workspaceId}/features`,
      { features: selectedFeatures },
      auth
    );

    setFeatures(selectedFeatures);

    setActiveWorkspace((prev) => ({
      ...prev,
      enabled_features: selectedFeatures,
    }));

    setWorkspaceData((prev) => ({
      ...prev,
      enabled_features: selectedFeatures,
    }));

    enqueueSnackbar("Workspace features updated successfully", {
      variant: "success",
    });

    window.dispatchEvent(new Event("workspaces-updated"));
  } catch (error) {
    enqueueSnackbar(
      errorDetail(error, "Failed to update workspace features"),
      { variant: "error" }
    );
  } finally {
    setSavingFeatures(false);
  }
};

  const openCreateWorkspacePopup = () => {
    setPopup({
      isOpen: true,
      title: "Add New Workspace",
      content: <CreateWorkspaceForm onSuccess={() => fetchWorkspaceDetails()} />,
      sendReq: null,
    });
  };

  return (
    <>
      <WorkspaceTabs />
      <Holder
        title={`Workspace Settings — ${activeWorkspace?.name || ""}`}
        action={
          <CustomBtn handle={openCreateWorkspacePopup} startIcon={<AddIcon />}>
            Add Workspace
          </CustomBtn>
        }
      >
        <Grid container spacing={4}>
          {/* General Information Section (Name, Description only) */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <SettingsIcon color="primary" fontSize="small" />
                  <Typography variant="h6" fontWeight={700}>
                    General Information
                  </Typography>
                </Stack> */}
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Update workspace name and description. These details are visible to all workspace members.
                </Typography>

                <Box component="form" onSubmit={formik.handleSubmit} noValidate>
                  <Stack spacing={2.5}>
                    <TextField
                      label="Workspace Name"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      error={formik.submitCount > 0 && Boolean(formik.errors.name)}
                      helperText={formik.submitCount > 0 && formik.errors.name}
                      fullWidth
                      disabled={loadingDetails}
                      required
                    />

                    <TextField
                      label="Description"
                      name="description"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      error={formik.submitCount > 0 && Boolean(formik.errors.description)}
                      helperText={formik.submitCount > 0 && formik.errors.description}
                      fullWidth
                      multiline
                      minRows={3}
                      disabled={loadingDetails}
                    />

                    <Stack direction="row" justifyContent="flex-end" mt={1}>
                      <LoadBtn loading={formik.isSubmitting} text="Save Details" />
                    </Stack>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Workspace Features Section */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  mb={1}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TuneIcon color="primary" fontSize="small" />
                    <Typography variant="h6" fontWeight={700}>
                      Workspace Features
                    </Typography>
                  </Stack>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={selectedFeatures.length === featureKeysArr.length}
                        onChange={handleToggleAll}
                      />
                    }
                    label={
                      <Typography variant="caption" fontWeight={600}>
                        {selectedFeatures.length === featureKeysArr.length
                          ? "Disable All"
                          : "Enable All"}
                      </Typography>
                    }
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" mb={2.5}>
                  Configure and toggle the detection modules and AI capabilities available in this workspace.
                </Typography>

                <Stack spacing={1.5} mb={3}>
                  {featureKeysArr.map(({ value, label }) => {
                    const isEnabled = selectedFeatures.includes(value);
                    return (
                      <Paper
                        key={value}
                        variant="outlined"
                        onClick={() => handleToggleFeature(value)}
                        sx={{
                          p: 1.5,
                          px: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          borderRadius: 2,
                          cursor: "pointer",
                          borderColor: isEnabled ? "primary.main" : "divider",
                          bgcolor: isEnabled ? "action.hover" : "background.paper",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: "primary.main",
                          },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          {isEnabled ? (
                            <CheckCircleOutlineIcon color="primary" fontSize="small" />
                          ) : (
                            <HighlightOffIcon color="disabled" fontSize="small" />
                          )}
                          <Box>
                            <Typography variant="body2" fontWeight={isEnabled ? 700 : 500}>
                              {label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Key: {value}
                            </Typography>
                          </Box>
                        </Stack>

                        <Switch
                          size="small"
                          checked={isEnabled}
                          onChange={() => handleToggleFeature(value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Paper>
                    );
                  })}
                </Stack>

                <Stack direction="row" justifyContent="flex-end">
                  <LoadBtn
                    loading={savingFeatures}
                    text="Save Features"
                    handle={handleSaveFeatures}
                    type="button"
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Holder>
    </>
  );
};

export default WorkspaceSettings;
